/**
 * 本地神谕引擎(关键词规则表)。
 * Phase 0:作为模拟器把"我要下雨"翻译成 rain 事件 + 诗性 flavor_text。
 * Phase 2:同一份规则表作为 LLM 超时/失败时的兜底(GDD 4.3 降级方案),
 *          保证世界永不失灵。逻辑严格遵循 GDD 4.2 的裁决规则。
 */
import type { EffectType, WorldEvent, EventSource } from '../types';
import { RHYTHM } from '../config';
import { crc32, pick } from '../engine/util';

// ── 效果关键词表(TOP 指令)。命中即映射到白名单枚举。──
const KEYWORDS: { type: EffectType; words: string[] }[] = [
  { type: 'meteor_shower', words: ['流星雨'] },
  { type: 'sky_split', words: ['撕裂', '天空撕裂', '对半', '分裂天'] },
  { type: 'aurora', words: ['极光'] },
  { type: 'thunderstorm', words: ['雷暴', '打雷', '闪电', '雷电', '暴雨'] },
  { type: 'whale', words: ['鲸', '巨鲸', '鲸鱼'] },
  { type: 'life_tide', words: ['生命', '诞生', '造人', '新烛民', '多点人'] },
  { type: 'world_tree', words: ['世界树', '大树', '神树'] },
  { type: 'statue', words: ['神像', '雕像', '塑像'] },
  { type: 'day_night_flip', words: ['昼夜', '天黑', '天亮', '白昼', '黑夜'] },
  { type: 'fireworks', words: ['烟花', '烟火', '放烟花', '礼花'] },
  { type: 'rainbow', words: ['彩虹'] },
  { type: 'meteor', words: ['流星', '许愿'] },
  { type: 'snow', words: ['雪', '下雪', '飘雪'] },
  { type: 'rain', words: ['雨', '下雨', '落雨', '来点雨'] },
  { type: 'wind', words: ['风', '起风', '刮风', '大风'] },
  { type: 'dance', words: ['跳舞', '蹦迪', '跳个舞', '起舞', '扭起来'] },
  { type: 'sky_color', words: ['变色', '天空变', '换个天', '粉色天', '紫色天'] },
];

const CLEAR_WORDS = ['天晴', '晴天', '放晴', '别下了', '停雨', '出太阳', '晴'];
const UNSAFE_WORDS = ['傻', '滚', '死', '垃圾', '智障', '骂', 'sb', 'nmsl', '狗东西'];

const DEFAULT_DUR: Partial<Record<EffectType, number>> = {
  rain: 14, snow: 16, wind: 12, fireworks: 8, rainbow: 12, meteor: 3, dance: 12, sky_color: 12,
  aurora: 16, thunderstorm: 14, day_night_flip: 12, life_tide: 8, whale: 10,
  epoch_leap: 6, statue: 12, world_tree: 12, meteor_shower: 20, sky_split: 12, dark_cloud: 8,
};

// ── flavor_text 文风:克制、诗性、偶尔幽默(GDD 4.2 规则5)──
const FLAVOR: Partial<Record<EffectType, string[]>> = {
  rain: ['神明低语:落雨。灵墟便落了雨。', '一场雨,替烛民们说了没说出口的话。', '雨落下来时,岛屿轻轻叹了口气。'],
  snow: ['神明说:下雪吧。于是天空开始沉默。', '雪落无声,烛民伸出手,接住第一片。'],
  wind: ['风起于神明的一次呼吸。', '起风了,岛上的光都朝一个方向倾斜。'],
  fireworks: ['神明放了一把火,烛民却当成了节日。', '烟花炸开,烛民集体抬头——原来天也会笑。'],
  rainbow: ['雨后,神明随手挂了一道桥。', '彩虹落下,像谁把光揉皱了又展开。'],
  meteor: ['一颗流星划过,有烛民悄悄许了愿。', '神明失手,掉了一粒光。'],
  dance: ['神明打了个响指,烛民便停不下脚。', '没有音乐,但烛民听见了神的节拍。'],
  sky_color: ['神明嫌天太旧,换了个颜色。', '天空换了副表情,烛民看呆了。'],
  aurora: ['神明抖开了一整幅光的帷幔。', '极光燃起,像天空在低声吟唱。'],
  thunderstorm: ['神明发了脾气,雷声替他说话。', '闪电劈下,岛屿抖了一下,又稳住了。'],
  day_night_flip: ['神明把昼夜拧反了,烛民一时分不清梦与醒。', '白天与黑夜换了位置,时间也会犯迷糊。'],
  life_tide: ['神明撒下一把光,落地便成了人。', '新的烛民从天而降,睁眼看见的第一样东西,是神。'],
  whale: ['一头巨鲸掠过云海,没有人知道它从哪来。', '鲸影穿云,岛上所有的光都静了一瞬。'],
  epoch_leap: ['旧的纪元合上,新的纪元睁开了眼。', '白光过后,世界长出了没见过的模样。'],
  statue: ['神像自岛心升起,替神明记住了所有名字。', '石像落成,它将比这里所有人活得都久。'],
  world_tree: ['一粒种子听懂了神谕,连夜长成了树。', '世界之树伸展,枝头挂满了没说完的愿望。'],
  meteor_shower: ['香火鼎盛!神明倾倒了一整条星河。', '流星雨落下,像神明把珍藏的光全给了出去。'],
  sky_split: ['神明们意见不合,于是天空,试着同时讨好所有人。', '一半晴一半雨,烛民分成两群,面面相觑。'],
  dark_cloud: ['一句不太好听的话,化作乌云飘过,烛民撑伞躲了躲。', '神明没听懂那句话,天空困惑地暗了一下。'],
};

function matchType(text: string): EffectType | null {
  for (const k of KEYWORDS) if (k.words.some((w) => text.includes(w))) return k.type;
  return null;
}

export function isUnsafe(text: string): boolean {
  return UNSAFE_WORDS.some((w) => text.toLowerCase().includes(w));
}
export function isClearIntent(text: string): boolean {
  return CLEAR_WORDS.some((w) => text.includes(w));
}

/** 评论文学评级:S(≥40字或成诗)/ A / B(GDD 4.2 规则2) */
export function gradeComment(text: string): 'S' | 'A' | 'B' {
  const len = text.length;
  const poetic = /[,,。;;、\n]/.test(text) && len >= 16;
  if (len >= 40 || (poetic && len >= 24)) return 'S';
  if (len >= 16 || poetic) return 'A';
  return 'B';
}

export function flavorFor(type: EffectType, raw?: string): string {
  const bank = FLAVOR[type] ?? ['神明说了句什么,世界照做了。'];
  const base = pick(bank);
  return raw && Math.random() < 0.4 ? base : base;
}

export function midHashOf(uid: string | number): string {
  return crc32(String(uid));
}

/** 从一条弹幕构造事件;synergy≥阈值时升级(弹幕合力 → 中神迹) */
export function eventFromDanmaku(text: string, source: EventSource, synergy = 1): WorldEvent | null {
  if (isUnsafe(text)) {
    return mk('dark_cloud', 0.5, source, '一朵乌云飘过。');
  }
  let type = matchType(text);
  if (!type && isClearIntent(text)) type = 'sky_color';
  if (!type) {
    // 无法匹配 → 最接近的,并戏剧化圆场(GDD 4.2 规则1)
    return mk('sky_color', 0.4, source, '神明说了凡人听不懂的话,天空困惑地闪了闪。');
  }
  let intensity = Math.min(1, 0.45 + synergy * 0.08);
  // 弹幕合力升级:同类 ≥ 阈值,轻神迹 → 对应中神迹
  if (synergy >= RHYTHM.danmakuSynergy) {
    const upgrade: Partial<Record<EffectType, EffectType>> = {
      rain: 'thunderstorm', fireworks: 'meteor_shower', dance: 'aurora', wind: 'thunderstorm', meteor: 'meteor_shower',
    };
    type = upgrade[type] ?? type;
    intensity = 0.85;
  }
  return mk(type, intensity, source);
}

/** 从一条评论构造事件(S 级刻石碑) */
export function eventFromComment(text: string, source: EventSource): WorldEvent {
  const grade = gradeComment(text);
  const type = matchType(text) ?? (grade === 'S' ? 'aurora' : grade === 'A' ? 'rainbow' : 'sky_color');
  const ev = mk(type, grade === 'S' ? 0.9 : grade === 'A' ? 0.65 : 0.45, source);
  ev.grade = grade;
  if (grade === 'S') {
    ev.carve_stone = true;
    ev.flavor_text = `圣诗入石:「${text.slice(0, 18)}${text.length > 18 ? '…' : ''}」`;
  }
  return ev;
}

function mk(type: EffectType, intensity: number, source: EventSource, flavor?: string): WorldEvent {
  return {
    type,
    intensity,
    duration_sec: DEFAULT_DUR[type] ?? 10,
    flavor_text: flavor ?? flavorFor(type, source.raw),
    source,
  };
}
